import {Subscription} from "rxjs";
import {FormsModule} from "@angular/forms";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';

import {NgSelectComponent} from "@ng-select/ng-select";

import {AdType} from "@numizmat/enumerations";
import {SearchFilterComponent} from "@numizmat/components";
import {
  AdSpecificationOptionApiService,
  AdSpecificationOptionStateService,
  AdSubcategorySpecificationApiService,
  AdSubcategorySpecificationStateService
} from "@numizmat/services/nomenclature";
import {
  AdSpecificationOptionResponse,
  AdSubcategorySpecificationResponse,
  AdSubcategoryAssociatedSpecificationResponse
} from "@numizmat/services-models/nomenclature";

@Component({
    selector: 'nz-ad-list-filter',
    imports: [
        SearchFilterComponent,
        NgSelectComponent,
        FormsModule
    ],
    templateUrl: './ad-list-filter.component.html',
    styleUrl: './ad-list-filter.component.scss'
})
export class AdListFilterComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private adSpecificationOptionApiService: AdSpecificationOptionApiService = inject(AdSpecificationOptionApiService);
  private adSpecificationOptionStateService: AdSpecificationOptionStateService = inject(AdSpecificationOptionStateService);
  private adSubcategorySpecificationApiService: AdSubcategorySpecificationApiService = inject(AdSubcategorySpecificationApiService);
  private adSubcategorySpecificationStateService: AdSubcategorySpecificationStateService = inject(AdSubcategorySpecificationStateService);

  private currentRoute: string = '';
  private subs: Subscription = new Subscription();

  protected adType: number = 0;
  protected adSubcategoryId: number = 0;
  protected activeFiltersCount: number = 0;
  protected adSpecificationIdsSingle: { [key: number]: number } = {};
  protected adSpecificationIdsMultiple: { [key: number]: number[] } = {};
  protected allAdSubcategorySpecifications: AdSubcategorySpecificationResponse[] = [];
  protected adSpecificationOptions: { [key: number]: AdSpecificationOptionResponse[] } = {};
  protected adSubcategoryAssociatedSpecifications: AdSubcategoryAssociatedSpecificationResponse[] = [];
  protected adTypeOptions: { id: number, name: string }[] = this.initializeAdTypeOptions();

  public ngOnInit(): void {
    this.currentRoute = this.router.url.split('?')[0];
    this.getAllAdSubcategorySpecifications();
    this.createQueryParamsChangeSubscription();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onChangeAdType(adType: number): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams:
        {adType: !!adType ? adType : null, page: 1},
    }).then();
  }

  protected onChangeAdSpecificationIdsSingle(adSpecificationId: number, options: AdSpecificationOptionResponse): void {
    if (!options) {
      delete this.adSpecificationIdsSingle[adSpecificationId];
    } else {
      this.adSpecificationIdsSingle[adSpecificationId] = options.adSpecificationOptionId;
    }
    const params: string[] = [];
    Object.keys(this.adSpecificationIdsSingle).forEach((adSpecificationId: string) => {
      params.push(`${adSpecificationId}_${this.adSpecificationIdsSingle[+adSpecificationId]}`);
    });
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams: {adSpecSin: params.length === 0 ? null : params.join('|'), page: 1},
    }).then();
  }

  protected onChangeAdSpecificationIdsMultiple(adSpecificationId: number, options: AdSpecificationOptionResponse[]): void {
    if (!options || options.length === 0) {
      delete this.adSpecificationIdsMultiple[adSpecificationId];
    } else {
      this.adSpecificationIdsMultiple[adSpecificationId] = options.map(x => x.adSpecificationOptionId);
    }
    const params: string[] = [];
    Object.keys(this.adSpecificationIdsMultiple).forEach((adSpecificationId: string) => {
      params.push(`${adSpecificationId}_${this.adSpecificationIdsMultiple[+adSpecificationId].join(',')}`);
    });
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams: {adSpecMul: params.length === 0 ? null : params.join('|'), page: 1},
    }).then();
  }

  protected onClearFilters(): void {
    this.router.navigate([this.currentRoute], {
      queryParamsHandling: 'merge',
      queryParams: {adType: null, adSpecSin: null, adSpecMul: null, page: 1},
    }).then();
  }

  protected getPlaceholder(adSpecificationName: string): string {
    return `Изберете ${adSpecificationName.toLowerCase()}...`;
  }

  private createQueryParamsChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.queryParams.subscribe({
      next: (queryParams: Params) => {
        this.adType = !!queryParams['adType'] ? Number.parseInt(queryParams['adType']) : 0;

        const adSpecSin: string[] = !!queryParams['adSpecSin'] ? queryParams['adSpecSin'].split('|') : [];
        let adSpecificationIdsSingle: { [key: number]: number } = {};
        adSpecSin.forEach(adSpec => {
          const adSpecifications = adSpec.split('_');
          adSpecificationIdsSingle[+adSpecifications[0]] = +adSpecifications[1];
        });
        this.adSpecificationIdsSingle = adSpecificationIdsSingle;

        const adSpecMul: string[] = !!queryParams['adSpecMul'] ? queryParams['adSpecMul'].split('|') : [];
        let adSpecificationIdsMultiple: { [key: number]: number[] } = {};
        adSpecMul.forEach(adSpec => {
          const adSpecifications = adSpec.split('_');
          adSpecificationIdsMultiple[+adSpecifications[0]] = adSpecifications[1].split(',').map(x => Number.parseInt(x));
        });
        this.adSpecificationIdsMultiple = adSpecificationIdsMultiple;

        const adSubcategoryId = !!queryParams['adSubcategoryId'] ? Number.parseInt(queryParams['adSubcategoryId']) : 0;
        if (adSubcategoryId !== this.adSubcategoryId) {
          this.adSubcategoryId = adSubcategoryId;
          this.mapAdSubcategorySpecifications();
        }

        this.getActiveFiltersCount();
      }
    });
    this.subs.add(subscription);
  }

  private getAllAdSubcategorySpecifications(): void {
    const subscription: Subscription = this.adSubcategorySpecificationStateService.getAllAdSubcategorySpecifications().subscribe({
      next: allAdSubcategorySpecifications => {
        if (!!allAdSubcategorySpecifications) {
          this.allAdSubcategorySpecifications = allAdSubcategorySpecifications;
          this.mapAdSubcategorySpecifications();
        } else {
          this.adSubcategorySpecificationApiService.getAllAdSubcategorySpecifications().subscribe({
            next: response => {
              this.adSubcategorySpecificationStateService.setAllAdSubcategorySpecifications(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private mapAdSubcategorySpecifications(): void {
    if (this.adSubcategoryId !== 0 && this.allAdSubcategorySpecifications.length !== 0) {
      const index: number = this.allAdSubcategorySpecifications.findIndex(x => x.adSubcategoryId === this.adSubcategoryId);
      if (index >= 0) {
        this.adSubcategoryAssociatedSpecifications = this.allAdSubcategorySpecifications[index].associatedSpecifications;
        for (const adSubcategoryAssociatedSpecification of this.adSubcategoryAssociatedSpecifications) {
          this.getAdSpecificationOptions(adSubcategoryAssociatedSpecification.adSpecificationId);
        }
      } else {
        this.adSubcategoryAssociatedSpecifications = [];
      }
    }
  }

  private getAdSpecificationOptions(adSpecificationId: number): void {
    const subscription: Subscription = this.adSpecificationOptionStateService.getAdSpecificationOptions(adSpecificationId).subscribe({
      next: adSpecificationOptions => {
        if (!!adSpecificationOptions) {
          this.adSpecificationOptions[adSpecificationId] = adSpecificationOptions;
        } else {
          this.adSpecificationOptionApiService.getAdSpecificationOptions(adSpecificationId).subscribe({
            next: response => {
              this.adSpecificationOptions[adSpecificationId] = response;
              this.adSpecificationOptionStateService.setAdSpecificationOptions(adSpecificationId, this.adSpecificationOptions[adSpecificationId]);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private initializeAdTypeOptions(): { id: number, name: string }[] {
    return [
      {
        id: 0,
        name: 'Всички'
      },
      {
        id: AdType.DirectSale,
        name: 'Обяви с фиксирана цена'
      },
      {
        id: AdType.Auction,
        name: 'Обяви с наддаване (аукциони)'
      }
    ];
  }

  private getActiveFiltersCount(): void {
    let currentActiveFiltersCount = 0;
    if (this.adType !== 0) {
      currentActiveFiltersCount++;
    }
    currentActiveFiltersCount += Object.keys(this.adSpecificationIdsSingle).length;
    currentActiveFiltersCount += Object.keys(this.adSpecificationIdsMultiple).length;
    this.activeFiltersCount = currentActiveFiltersCount;
  }
}
